import type { TextLineHeightModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import LineSpacing from "@svg/modifiers/line-spacing";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { LINE_HEIGHT_OPTIONS } from "@v2/features/modifiers/constants";
import { ChevronDown } from "lucide-react";
import { parseUnit } from "replo-runtime/shared/utils/units";

interface LineHeightModifierProps {
  onChange: (modifiers: TextLineHeightModifierValue[]) => void;
}

const LineHeightModifier: React.FC<LineHeightModifierProps> = ({
  onChange,
}) => {
  // NOTE (Derek, 2026-02-19): computedStyles.lineHeight is in px (e.g. "24px").
  // We need fontSize to convert to a multiplier. If fontSize is "16px" and lineHeight is "24px",
  // the multiplier is 24/16 = 1.5.
  const currentValue = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      const lineHeight = computedStyles.lineHeight;
      const fontSize = computedStyles.fontSize;
      if (!lineHeight || lineHeight === "normal") {
        return null;
      }
      const lhMatch = lineHeight.match(/^(\d+(?:\.\d+)?)/);
      const fsMatch = fontSize?.match(/^(\d+(?:\.\d+)?)/);
      if (!lhMatch?.[1] || !fsMatch?.[1]) {
        return null;
      }
      const lhPx = Number.parseFloat(lhMatch[1]);
      const fsPx = Number.parseFloat(fsMatch[1]);
      if (isNaN(lhPx) || isNaN(fsPx) || fsPx === 0) {
        return null;
      }
      // Return as multiplier (e.g. 1.5), rounded to 3 decimal places
      return Math.round((lhPx / fsPx) * 1000) / 1000;
    },
  });

  const displayValue = currentValue != null ? `${currentValue * 100}%` : "Auto";

  const menuOptions = LINE_HEIGHT_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value === null ? null : `${option.value * 100}`,
  }));

  const handleChange = (newValue: string | null) => {
    if (newValue === null || newValue.toLowerCase() === "auto") {
      onChange([{ type: "lineHeight" as const, value: null }]);
      return;
    }

    const result = parseUnit(
      newValue,
      { value: 100, unit: "%" },
      "lineHeight",
      "%",
    );
    onChange([
      {
        type: "lineHeight" as const,
        value: Number(result.value) / 100,
      },
    ]);
  };

  return (
    <div className="line-height-modifier">
      <LengthInputSelector
        field="lineHeight"
        value={displayValue}
        startEnhancer={<LineSpacing />}
        onChange={handleChange}
        placeholder="100%"
        metrics={["%"]}
        unitDefaults={{ value: "100", unit: "%" }}
        menuOptions={menuOptions}
        minValues={{ "%": 100 }}
        inputClassName="w-full"
        endEnhancer={() => <ChevronDown size={12} />}
        allowedNonUnitValues={["Auto", "auto"]}
        transformValue={(value: string) => {
          return value.toLowerCase() === "auto" ? "Auto" : value;
        }}
      />
    </div>
  );
};

export default LineHeightModifier;
